/**
 * The Ledger design system, applied to the Chakra theme.
 *
 * `src/styles/tokens.css` holds every colour, size, space and radius; this
 * config only points Chakra's names at those variables, so one source drives
 * both the component library and any plain CSS. The CSS variables already
 * switch with the theme (`<html data-theme="dark">`), which is why each colour
 * below is the same variable under `_light` and `_dark`: writing both keys is
 * what overrides the app's older `{ _light, _dark }` values in the deep merge.
 *
 * App code uses the Ledger names (`surface.2`, `fg.3`, `border.strong`, `ok`,
 * …). The Chakra names its own recipes and defaults read (`bg.muted`,
 * `fg.muted`, `border.emphasized`, …) stay as a bridge onto the same
 * variables. See docs/design-system/TOKEN-MAP.md for every mapping and
 * docs/design-system/CLEANUP-PLAN.md for the bridge list.
 *
 * Spec: specs/design-system/ledger-foundations.feature
 */
import { defineConfig } from "@chakra-ui/react";

/** A colour that is one token file variable in both themes. */
const ledger = (variable: string) => ({
  value: { _light: `var(${variable})`, _dark: `var(${variable})` },
});

/** A non-colour scale step: one token file variable. */
const scale = (variable: string) => ({ value: `var(${variable})` });

/**
 * A Chakra colour palette (`colorPalette="…"`) expressed in Ledger roles.
 * `solid`/`contrast` draw filled controls, `subtle`/`muted` the soft tints,
 * `fg` the text on those tints, `border` the outline Chakra's own outline
 * variants read (`colorPalette.border`; the emphasized colour unless given).
 */
const palette = (roles: {
  solid: string;
  hover: string;
  contrast: string;
  fg: string;
  subtle: string;
  muted: string;
  emphasized: string;
  border?: string;
}) => ({
  solid: ledger(roles.solid),
  hover: ledger(roles.hover),
  contrast: ledger(roles.contrast),
  fg: ledger(roles.fg),
  subtle: ledger(roles.subtle),
  muted: ledger(roles.muted),
  emphasized: ledger(roles.emphasized),
  border: ledger(roles.border ?? roles.emphasized),
  focusRing: ledger("--color-focus"),
});

/** A status hue: filled, soft tint, and the text colour for that tint. */
const statusPalette = (status: "ok" | "warn" | "err" | "info") =>
  palette({
    solid: `--color-${status}`,
    hover: `--color-${status}`,
    contrast: "--color-accent-fg",
    fg: `--color-${status}-soft-fg`,
    subtle: `--color-${status}-soft`,
    muted: `--color-${status}-soft`,
    emphasized: `--color-${status}`,
  });

/**
 * A Ledger status family: `ok`, `ok.soft`, `ok.softFg`, plus the palette
 * roles, so `colorPalette="ok"` draws a status chip, badge or button in that
 * status's colours (`ok.solid` filled, `ok.subtle` the soft tint, `ok.fg` the
 * text on it).
 */
const statusFamily = (status: "ok" | "warn" | "err" | "info") => ({
  ...statusPalette(status),
  DEFAULT: ledger(`--color-${status}`),
  soft: ledger(`--color-${status}-soft`),
  softFg: ledger(`--color-${status}-soft-fg`),
});

/** A colour computed from token file variables, the same in both themes. */
const computed = (value: string) => ({
  value: { _light: value, _dark: value },
});

/**
 * A chart series hue as a palette (`colorPalette="chart1"`), for categorical
 * data marks: a run's dot, a series swatch, a category chip that must keep a
 * hue (D-06, D-07). A category is never told apart by a status colour. The
 * Ledger has no soft tint for the chart hues, so the tint is the hue at the
 * chart fill alpha over the surface, and the text on it is the hue darkened
 * (light) or lightened (dark) towards the text colour: 6.3:1 or more on
 * `subtle`, 5.2:1 on `muted`, in both themes. Text on the solid hue is
 * `--color-step-fg`, the glyph colour the step families use (5:1 or more).
 */
const chartPalette = (n: 1 | 2 | 3) => {
  const hue = `var(--color-chart-${n})`;
  return {
    DEFAULT: ledger(`--color-chart-${n}`),
    solid: ledger(`--color-chart-${n}`),
    hover: ledger(`--color-chart-${n}`),
    emphasized: ledger(`--color-chart-${n}`),
    border: ledger(`--color-chart-${n}`),
    contrast: ledger("--color-step-fg"),
    subtle: computed(
      `color-mix(in srgb, ${hue} calc(var(--color-chart-fill-alpha) * 100%), var(--color-surface))`,
    ),
    muted: computed(
      `color-mix(in srgb, ${hue} calc(var(--color-chart-fill-alpha) * 200%), var(--color-surface))`,
    ),
    fg: computed(`color-mix(in srgb, ${hue} 70%, var(--color-fg))`),
    focusRing: ledger("--color-focus"),
  };
};

const accentPalette = palette({
  solid: "--color-accent",
  hover: "--color-accent-hover",
  contrast: "--color-accent-fg",
  fg: "--color-accent-soft-fg",
  subtle: "--color-accent-soft",
  muted: "--color-accent-soft",
  emphasized: "--color-accent-active",
});

/** The neutral palette: `gray`, and every categorical palette Chakra ships. */
const neutralPalette = palette({
  solid: "--color-surface-3",
  hover: "--color-border",
  contrast: "--color-fg",
  fg: "--color-fg-2",
  subtle: "--color-surface-2",
  muted: "--color-surface-2",
  emphasized: "--color-border-strong",
  border: "--color-border",
});

export const ledgerThemeConfig = defineConfig({
  globalCss: {
    body: {
      background: "var(--color-bg)",
      color: "var(--color-fg)",
      fontSize: "var(--text-13)",
      lineHeight: "var(--leading-normal)",
    },
  },
  theme: {
    tokens: {
      fonts: {
        heading: { value: "var(--font-sans)" },
        body: { value: "var(--font-sans)" },
        mono: { value: "var(--font-mono)" },
      },
      // Chakra's keys stay; their sizes become the Ledger scale. 4xl and up
      // are display sizes the Ledger has no step for; they are left to the
      // screens that use them (docs/design-system/DEVIATIONS.md, D-04).
      fontSizes: {
        "2xs": scale("--text-11"),
        xs: scale("--text-12"),
        sm: scale("--text-13"),
        md: scale("--text-14"),
        lg: scale("--text-16"),
        xl: scale("--text-18"),
        "2xl": scale("--text-22"),
        "3xl": scale("--text-28"),
      },
      fontWeights: {
        normal: scale("--weight-regular"),
        medium: scale("--weight-medium"),
        semibold: scale("--weight-semibold"),
        // Nothing in the Ledger is bolder than semibold.
        bold: scale("--weight-semibold"),
        extrabold: scale("--weight-semibold"),
        black: scale("--weight-semibold"),
      },
      radii: {
        "2xs": scale("--radius-1"),
        xs: scale("--radius-1"),
        sm: scale("--radius-2"),
        md: scale("--radius-3"),
        lg: scale("--radius-3"),
        xl: scale("--radius-4"),
        "2xl": scale("--radius-4"),
        "3xl": scale("--radius-4"),
        "4xl": scale("--radius-4"),
        full: scale("--radius-pill"),
      },
      durations: {
        fastest: scale("--dur-1"),
        faster: scale("--dur-1"),
        fast: scale("--dur-2"),
        moderate: scale("--dur-2"),
        slow: scale("--dur-3"),
        slower: scale("--dur-3"),
        slowest: scale("--dur-3"),
      },
    },
    semanticTokens: {
      shadows: {
        "2xs": ledger("--shadow-1"),
        xs: ledger("--shadow-1"),
        sm: ledger("--shadow-1"),
        md: ledger("--shadow-2"),
        lg: ledger("--shadow-3"),
        xl: ledger("--shadow-3"),
        "2xl": ledger("--shadow-3"),
      },
      colors: {
        // The Ledger's own names, used by app code. Each is one tokens.css
        // variable; the digit keys resolve as `fg.2` and `--chakra-colors-fg-2`.
        surface: {
          DEFAULT: ledger("--color-surface"),
          2: ledger("--color-surface-2"),
          3: ledger("--color-surface-3"),
          selected: ledger("--color-surface-selected"),
          inverse: ledger("--color-surface-inverse"),
        },
        // `bg`, `fg` and `border` mix the Ledger names with the bridge names
        // Chakra's own recipes and defaults read (`bg.muted`, `fg.muted`,
        // `border.emphasized`, …). The bridge names stay pointed at tokens.css
        // so Chakra's components draw in Ledger colours; app code uses the
        // Ledger names. docs/design-system/CLEANUP-PLAN.md lists the bridge.
        bg: {
          DEFAULT: ledger("--color-bg"),
          panel: ledger("--color-surface"),
          muted: ledger("--color-surface-2"),
          subtle: ledger("--color-surface-2"),
          emphasized: ledger("--color-surface-3"),
          inverted: ledger("--color-surface-inverse"),
          error: ledger("--color-err-soft"),
          warning: ledger("--color-warn-soft"),
          success: ledger("--color-ok-soft"),
          info: ledger("--color-info-soft"),
        },
        fg: {
          DEFAULT: ledger("--color-fg"),
          2: ledger("--color-fg-2"),
          3: ledger("--color-fg-3"),
          disabled: ledger("--color-fg-disabled"),
          placeholder: ledger("--color-fg-placeholder"),
          inverse: ledger("--color-fg-inverse"),
          muted: ledger("--color-fg-2"),
          subtle: ledger("--color-fg-3"),
          inverted: ledger("--color-fg-inverse"),
          error: ledger("--color-err"),
          warning: ledger("--color-warn"),
          success: ledger("--color-ok"),
          info: ledger("--color-info"),
        },
        border: {
          DEFAULT: ledger("--color-border"),
          2: ledger("--color-border-2"),
          strong: ledger("--color-border-strong"),
          input: ledger("--color-border-input"),
          muted: ledger("--color-border-2"),
          subtle: ledger("--color-border-2"),
          emphasized: ledger("--color-border-strong"),
          inverted: ledger("--color-border-strong"),
          error: ledger("--color-err"),
          warning: ledger("--color-warn"),
          success: ledger("--color-ok"),
          info: ledger("--color-info"),
        },
        ok: statusFamily("ok"),
        warn: statusFamily("warn"),
        err: statusFamily("err"),
        info: statusFamily("info"),
        // Categorical data hues (`--color-chart-1..3`) as palettes. A data
        // mark that must keep a hue takes one of these, never a status
        // family; the grid is `chart.grid`.
        chart1: chartPalette(1),
        chart2: chartPalette(2),
        chart3: chartPalette(3),
        chart: { grid: ledger("--color-chart-grid") },
        // Orange was VoxTest's call-to-action colour; in the Ledger that role
        // is the accent. Raw `orange.500`-style steps are moved screen by screen.
        orange: accentPalette,
        // The Ledger's own name for the same role, used by the primitives
        // (`<Button variant="primary">`, `<Chip tone="accent">`) and by app
        // code. `accent.fg` keeps Chakra's palette meaning (text on the soft
        // tint, the same colour as `accent.softFg`); text on the solid accent
        // is `accent.contrast`.
        accent: {
          ...accentPalette,
          DEFAULT: ledger("--color-accent"),
          active: ledger("--color-accent-active"),
          soft: ledger("--color-accent-soft"),
          softFg: ledger("--color-accent-soft-fg"),
        },
        gray: neutralPalette,
        green: statusPalette("ok"),
        red: statusPalette("err"),
        yellow: statusPalette("warn"),
        blue: statusPalette("info"),
        // Chakra's other categorical palettes draw neutral (D-07): a category
        // is told by its label, or takes a chart hue (`chart1..3`). App code
        // uses none of them; a stray `colorPalette="purple"` draws a Ledger
        // grey rather than Chakra's stock hue. The avatar marks keep their
        // purple, teal, cyan and pink under `avatar.*` (theme/avatarMarks.ts).
        purple: neutralPalette,
        teal: neutralPalette,
        cyan: neutralPalette,
        pink: neutralPalette,
      },
    },
  },
});
